import {alpha, createTheme, type PaletteMode, responsiveFontSizes, type ThemeOptions} from "@mui/material/styles";

const baseThemeOptions: ThemeOptions = {
    shape: {
        borderRadius: 4,
    },
    typography: {
        fontFamily: '"Inter", "Segoe UI", sans-serif',
        h1: {
            fontFamily: '"Manrope", "Segoe UI", sans-serif',
            fontWeight: 800,
        },
        h2: {
            fontFamily: '"Manrope", "Segoe UI", sans-serif',
            fontWeight: 800,
        },
        h3: {
            fontFamily: '"Manrope", "Segoe UI", sans-serif',
            fontWeight: 800,
        },
        h4: {
            fontFamily: '"Manrope", "Segoe UI", sans-serif',
            fontWeight: 800,
        },
        h5: {
            fontFamily: '"Manrope", "Segoe UI", sans-serif',
            fontWeight: 700,
        },
        h6: {
            fontFamily: '"Manrope", "Segoe UI", sans-serif',
            fontWeight: 700,
        },
        button: {
            textTransform: "none",
            fontWeight: 700,
        },
    },
    components: {
        MuiAppBar: {
            styleOverrides: {
                root: {
                    boxShadow: "none",
                    backgroundImage: "none",
                },
            },
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                contained: ({theme}) => ({
                    "&.Mui-disabled": {
                        color:
                            theme.palette.mode === "dark"
                                ? alpha(theme.palette.text.primary, 0.72)
                                : alpha(theme.palette.text.primary, 0.46),
                        borderColor:
                            theme.palette.mode === "dark"
                                ? alpha(theme.palette.primary.light, 0.18)
                                : alpha(theme.palette.primary.dark, 0.12),
                        boxShadow: "none",
                    },
                }),
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: "none",
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: ({theme}) => ({
                    transition: "background-color 140ms ease, color 140ms ease",
                    "&:hover": {
                        backgroundColor:
                            theme.palette.mode === "light"
                                ? alpha(theme.palette.primary.main, 0.08)
                                : alpha(theme.palette.primary.main, 0.18),
                    },
                    "&.Mui-focusVisible": {
                        backgroundColor:
                            theme.palette.mode === "light"
                                ? alpha(theme.palette.primary.main, 0.12)
                                : alpha(theme.palette.primary.main, 0.24),
                    },
                    "&.Mui-selected": {
                        backgroundColor:
                            theme.palette.mode === "light"
                                ? alpha(theme.palette.primary.main, 0.12)
                                : alpha(theme.palette.primary.main, 0.24),
                    },
                    "&.Mui-selected:hover": {
                        backgroundColor:
                            theme.palette.mode === "light"
                                ? alpha(theme.palette.primary.main, 0.16)
                                : alpha(theme.palette.primary.main, 0.3),
                    },
                }),
            },
        },
    },
};

const getModeThemeOptions = (mode: PaletteMode): ThemeOptions => {
    switch (mode) {
        case "dark":
            return {
                palette: {
                    mode: "dark",
                    primary: {
                        main: "#7fc7f0",
                        dark: "#4f97c1",
                        light: "#b4e0f7",
                        contrastText: "#0d2230",
                    },
                    secondary: {
                        main: "#ffbe78",
                        dark: "#cc8b48",
                        light: "#ffd6aa",
                        contrastText: "#2f1a08",
                    },
                    background: {
                        default: "#0d141b",
                        paper: "#16212c",
                    },
                    text: {
                        primary: "#e8f0f7",
                        secondary: "#98aabc",
                    },
                    divider: "rgba(127, 199, 240, 0.16)",
                },
                components: {
                    MuiCssBaseline: {
                        styleOverrides: {
                            "*": {
                                scrollbarWidth: "thin",
                                scrollbarColor: `${alpha("#7fc7f0", 0.48)} ${alpha("#16212c", 0.42)}`,
                            },
                            "*::-webkit-scrollbar": {
                                width: 10,
                                height: 10,
                            },
                            "*::-webkit-scrollbar-track": {
                                backgroundColor: alpha("#16212c", 0.42),
                            },
                            "*::-webkit-scrollbar-thumb": {
                                backgroundColor: alpha("#7fc7f0", 0.48),
                                borderRadius: 999,
                                border: `2px solid ${alpha("#0d141b", 0.92)}`,
                            },
                            "*::-webkit-scrollbar-thumb:hover": {
                                backgroundColor: alpha("#7fc7f0", 0.68),
                            },
                            "*::-webkit-scrollbar-corner": {
                                backgroundColor: alpha("#16212c", 0.42),
                            },
                            body: {
                                backgroundColor: "#0d141b",
                                color: "#e8f0f7",
                            },
                        },
                    },
                },
            };
        case "light":
        default:
            return {
                palette: {
                    mode: "light",
                    primary: {
                        main: "#1e78ae",
                        dark: "#155d88",
                        light: "#5ea7d0",
                        contrastText: "#f7fbff",
                    },
                    secondary: {
                        main: "#d28636",
                        dark: "#9f611f",
                        light: "#e8ae72",
                        contrastText: "#fffaf5",
                    },
                    background: {
                        default: "#f4f8fb",
                        paper: "#ffffff",
                    },
                    text: {
                        primary: "#12202d",
                        secondary: "#5c6f81",
                    },
                    divider: "rgba(30, 120, 174, 0.14)",
                },
                components: {
                    MuiCssBaseline: {
                        styleOverrides: {
                            body: {
                                backgroundColor: "#f4f8fb",
                                color: "#12202d",
                            },
                        },
                    },
                },
            };
    }
};

export const getAppTheme = (mode: PaletteMode = "light") =>
    responsiveFontSizes(createTheme(baseThemeOptions, getModeThemeOptions(mode)));
